import { useCallback, useMemo } from 'react';
import { useTracker } from '@archipro-website/tracker';
import type { Platform } from '@rocco/ui/social-sharing/utils/platform';
import { capitalizeFirstLetter } from '~/utils/stringHelper';
import { shareNative, type ShareOptions } from '../utils/sharing';

/** `ShareSubmit`'s `ExtraData` when the phone's own share sheet completed the share. */
export const NATIVE_SHARE_EXTRA_DATA = 'NativeShare';

/**
 * The item a share control shares when it is not the page the visitor is on:
 * a tile in a listing or in a detail page's rail.
 *
 * The server credits a share to the professional behind the page URL unless
 * the event names an item, so without this a tile's share counts for the page
 * hosting it — its professional's share tally and "page shared" milestone —
 * and on a listing page for nobody.
 */
export interface ShareSubject {
    libraryItemID: number;
    /** The item's professional, when known. The server reads the item's own otherwise. */
    professionalID?: number;
}

/**
 * The share funnel's two events, logged the same way by every share control:
 * `ShareReveal` when the visitor asks to share, and `ShareSubmit` naming where
 * the share went once it has gone. A page-level control passes no subject and
 * the events belong to the page; a tile passes its own item.
 *
 * A phone's share hands the choice of target to the OS, so it logs
 * `ShareSubmit` only once the OS reports the share made. Dismissing the OS
 * sheet resolves nothing and logs nothing.
 */
export const useShareTracking = (subject?: ShareSubject) => {
    const tracker = useTracker();
    const libraryItemID = subject?.libraryItemID;
    const professionalID = subject?.professionalID;

    const subjectData = useMemo(
        () =>
            libraryItemID
                ? {
                      LibraryItemID: libraryItemID,
                      ...(professionalID && { ProfessionalID: professionalID }),
                  }
                : undefined,
        [libraryItemID, professionalID],
    );

    const logReveal = useCallback(() => {
        tracker.log('ShareReveal', {
            url: new URL(window.location.href),
            ...(subjectData && { data: subjectData }),
        });
    }, [tracker, subjectData]);

    const logSubmit = useCallback(
        (platform: Platform | typeof NATIVE_SHARE_EXTRA_DATA) => {
            tracker.log('ShareSubmit', {
                url: new URL(window.location.href),
                data: {
                    ...subjectData,
                    ExtraData: capitalizeFirstLetter(platform),
                },
            });
        },
        [tracker, subjectData],
    );

    const shareNativeTracked = useCallback(
        async (options: ShareOptions) => {
            const result = await shareNative(options);
            if (result.success) logSubmit(NATIVE_SHARE_EXTRA_DATA);
            return result;
        },
        [logSubmit],
    );

    return useMemo(
        () => ({ logReveal, logSubmit, shareNative: shareNativeTracked }),
        [logReveal, logSubmit, shareNativeTracked],
    );
};
